import { logger } from '@/utils/logger';

const RELOAD_FLAG = 'invyo:chunk-reload-at';
const RELOAD_WINDOW_MS = 60_000;

/**
 * Installs a listener for Vite's `vite:preloadError`, fired when a dynamic
 * import (a lazy route chunk) fails to load, most often because a new deploy
 * rotated the chunk hashes while the user's tab was still open.
 *
 * On the first failure we reload once so the browser picks up the fresh
 * index.html and hashes. A timestamped flag in sessionStorage guards against a
 * reload loop: if we already reloaded within the last minute (the CDN is truly
 * down, or the user is offline), we let the error bubble to the
 * RouteErrorBoundary instead of reloading forever.
 *
 * `win` is injectable for testing; it defaults to the real window. The event
 * only fires in a production build (Vite's preload helper), never in dev.
 */
export function installChunkReloadHandler(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    const last = Number(win.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) {
      // Already reloaded recently; don't loop. Let the error boundary take over.
      logger.error('Chunk preload failed again after a recent reload', event);
      return;
    }
    event.preventDefault();
    win.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    win.location.reload();
  });
}
